/**
 * @file Parent board modal form field renderers.
 */

import { appData } from "../../app/state/state.js";
import {
    formatCaseFileNumber,
    formatCurrency,
} from "../utils/displayUtils.js";
import {
    AUTO_GROWING_TEXTAREA_CLASSES,
    FIELD_WRAPPER_CLASSES,
    FIXED_TEXTAREA_CLASSES,
    NATIVE_INPUT_CLASSES,
    READ_ONLY_VALUE_CLASSES,
    REQUIRED_MARK_HTML,
    getFieldLabelHtml,
} from "./nativeFieldStyles.js";
import {
    createSelectControl,
} from "../ui/controls/selectControl.js";

const WEEKDAY_OPTIONS = [
  { value: "same_day", label: "On the same day" },
  { value: "monday", label: "On Monday" },
  { value: "tuesday", label: "On Tuesday" },
  { value: "wednesday", label: "On Wednesday" },
  { value: "thursday", label: "On Thursday" },
  { value: "friday", label: "On Friday" },
  { value: "saturday", label: "On Saturday" },
  { value: "sunday", label: "On Sunday" },
];

const MONTHLY_RECURRENCE_OPTIONS = [
  { value: "same_date", label: "On the same date" },
  { value: "first_day", label: "On the first day of the month" },
  { value: "last_day", label: "On the last day of the month" },
];

const RECURRENCE_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const RECURRENCE_ANCHOR_OPTIONS = [
  { value: "dueDate", label: "Due date" },
  { value: "completionDate", label: "Date completed" },
];

function fieldHtml(label, controlHtml, options = {}) {
  const wrapperClass = [FIELD_WRAPPER_CLASSES, options.className || ""]
    .filter(Boolean)
    .join(" ");
  return `<div class="${wrapperClass}">${getFieldLabelHtml(label, Boolean(options.required))}${controlHtml}</div>`;
}

function inputHtml({
  type = "text",
  name,
  value = "",
  placeholder = "",
  required = false,
  className = NATIVE_INPUT_CLASSES,
  extraAttrs = "",
}) {
  const placeholderAttr = placeholder ? ` placeholder="${placeholder}"` : "";
  return `<input type="${type}" name="${name}" value="${value}" class="${className}"${placeholderAttr}${required ? " required" : ""}${extraAttrs ? ` ${extraAttrs}` : ""}>`;
}

function textareaHtml({
  name,
  value = "",
  placeholder = "",
  required = false,
  className = FIXED_TEXTAREA_CLASSES,
  extraAttrs = "",
}) {
  const placeholderAttr = placeholder ? ` placeholder="${placeholder}"` : "";
  return `<textarea name="${name}" class="${className}"${placeholderAttr}${required ? " required" : ""}${extraAttrs ? ` ${extraAttrs}` : ""}>${value}</textarea>`;
}

function readOnlyValueHtml(name, value = "") {
  return `<input type="hidden" name="${name}" value="${value}"><p class="${READ_ONLY_VALUE_CLASSES}">${value}</p>`;
}

/**
 * Renders form fields for a registered parent form type.
 * @param {"patient"|"project"|"asset"|"task"} type - Parent form type.
 * @param {object} itemData - Current modal form data.
 * @returns {string} Field HTML.
 */
export function getParentFormFields(type, itemData) {
  switch (type) {
    case "patient":
      return getPatientFormFields(itemData);
    case "project":
      return getProjectFormFields(itemData);
    case "asset":
      return getAssetFormFields(itemData);
    case "task":
      return getTaskFormFields(itemData);
    default:
      throw new Error(`Unsupported parent form type: ${type}`);
  }
}

/** Generates HTML fields for the Patient form. */
export function getPatientFormFields(itemData) {
  const val = (field, def = "") => itemData?.[field] ?? def;
  const isEditing = !!itemData?.id;
  const caseFileNumber = isEditing
    ? val("case_file_number")
    : formatCaseFileNumber(appData?.config?.nextCaseFileNumber || 0);
  return `
     <h4 class="font-semibold text-lg text-gray-700 dark:text-gray-300 border-b dark:border-gray-600">Patient Details</h4>
     <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         ${fieldHtml("Patient Name", inputHtml({ name: "name", value: val("name"), required: true }), { required: true })}
         ${fieldHtml("Card ID", inputHtml({ name: "card_id", value: val("card_id") }))}
         ${fieldHtml("Phone Number", inputHtml({ type: "tel", name: "phone", value: val("phone") }))}
         ${fieldHtml("Email", inputHtml({ type: "email", name: "email", value: val("email") }))}
     </div>

     <h4 class="font-semibold text-lg text-gray-700 dark:text-gray-300 border-b !mt-5 dark:border-gray-600">Case Details</h4>
     <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         ${fieldHtml("Case File #", readOnlyValueHtml("case_file_number", caseFileNumber))}
         ${fieldHtml("Case Manager", '<div data-parent-people-control="case_manager"></div>')}

         ${fieldHtml("Case Type", '<div id="form-patient-types-choices"></div>', { required: true })}
         ${fieldHtml("Provider(s)", '<div id="form-provider-choices"></div>')}

         <div id="dynamic-fields-anchor" class="contents"></div>

         ${fieldHtml("Urgency", '<div data-parent-scalar-control="priority"></div>')}
         ${fieldHtml("Status", '<div data-parent-scalar-control="status"></div>')}

         ${fieldHtml("Case Value", inputHtml({ name: "case_value", value: formatCurrency(val("case_value")), placeholder: "$", className: `${NATIVE_INPUT_CLASSES} currency-input`, extraAttrs: 'inputmode="decimal"' }))}
         ${fieldHtml("Payment Method", '<div data-parent-scalar-control="payment_method"></div>')}

         ${fieldHtml("First Seen", '<div data-parent-scalar-control="first_seen"></div>')}
         ${fieldHtml("Next Appointment", '<div data-parent-scalar-control="next_appointment"></div>')}

         ${fieldHtml("Details", textareaHtml({ name: "comments", value: val("comments") }), { className: "md:col-span-2" })}
     </div>
 `;
}

/** Generates HTML fields for the Project form. */
export function getProjectFormFields(itemData) {
  const val = (field, def = "") => itemData?.[field] ?? def;
  return `
     <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         ${fieldHtml("Project Name", inputHtml({ name: "name", value: val("name"), required: true }), { required: true, className: "md:col-span-2" })}

         ${fieldHtml("Type(s)", '<div id="form-project-types-choices"></div>')}
         ${fieldHtml("Project Manager(s)", '<div id="form-project_manager-choices"></div>')}

         <div id="dynamic-fields-anchor" class="contents"></div>

         ${fieldHtml("Urgency", '<div data-parent-scalar-control="priority"></div>')}
         ${fieldHtml("Status", '<div data-parent-scalar-control="status"></div>')}

         ${fieldHtml("Delivery Date", '<div data-parent-scalar-control="delivery_date"></div>')}
         ${fieldHtml("Budget", inputHtml({ name: "budget", value: formatCurrency(val("budget")), placeholder: "$", className: `${NATIVE_INPUT_CLASSES} currency-input`, extraAttrs: 'inputmode="decimal"' }))}

         ${fieldHtml("Details", textareaHtml({ name: "comments", value: val("comments") }), { className: "md:col-span-2" })}
     </div>
 `;
}

/** Generates HTML fields for the Asset form. */
export function getAssetFormFields(itemData) {
  const val = (field, def = "") => itemData?.[field] ?? def;

  return `
     <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         ${fieldHtml("Name", inputHtml({ name: "name", value: val("name"), required: true }), { required: true })}
         ${fieldHtml("Serial Number", inputHtml({ name: "serial_number", value: val("serial_number") }))}

         ${fieldHtml("Asset Type(s)", '<div id="form-asset-types-choices"></div>')}

         ${fieldHtml("Description", textareaHtml({ name: "description", value: val("description"), placeholder: "Colour, location, size, etc...", className: AUTO_GROWING_TEXTAREA_CLASSES, extraAttrs: 'rows="1" data-auto-grow="true"' }))}

         <div id="dynamic-fields-anchor" class="contents"></div>

         ${fieldHtml("Purchase Date", '<div data-parent-scalar-control="purchase_date"></div>')}
         ${fieldHtml("Purchase Price", inputHtml({ name: "purchase_price", value: formatCurrency(val("purchase_price")), placeholder: "$0.00", className: `${NATIVE_INPUT_CLASSES} currency-input`, extraAttrs: 'inputmode="decimal"' }))}
         ${fieldHtml("Supplier", inputHtml({ name: "supplier", value: val("supplier") }))}
         ${fieldHtml("Warranty", inputHtml({ name: "warranty", value: val("warranty") }))}
         ${fieldHtml("Installer", inputHtml({ name: "installer", value: val("installer") }))}
         ${fieldHtml("Installed On", '<div data-parent-scalar-control="installed_on"></div>')}
         ${fieldHtml("Service Frequency (Months)", inputHtml({ type: "number", name: "servicing_requirements", value: val("servicing_requirements"), placeholder: "e.g., 12", extraAttrs: 'min="1" step="1" inputmode="numeric"' }))}
         ${fieldHtml("Equipment Status", '<div data-parent-scalar-control="equipment_status"></div>')}
         ${fieldHtml("Details", textareaHtml({ name: "comments", value: val("comments") }), { className: "md:col-span-2" })}
     </div>
 `;
}

/** Generates HTML fields for the Task form. */
export function getTaskFormFields(itemData) {
  const val = (field, def = "") => itemData?.[field] ?? def;
  const recurrence = getTaskRecurrenceFormState(itemData);
  return `
     <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
         <input type="hidden" name="recurrence_enabled" value="${recurrence.isActive ? "true" : "false"}">
         <div id="task-recurrence-fields" class="md:col-span-2 rounded-lg bg-green-50 p-3 dark:bg-green-500/10 ${recurrence.isActive ? "" : "hidden"}">
            <h4 class="mb-3 text-sm font-semibold text-green-700 dark:text-green-300">Recurring Task Schedule</h4>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                ${fieldHtml("Repeat", '<div data-task-recurrence-control="frequency"></div>', { required: true })}
                <div id="task-recurrence-modifier-wrap" class="${FIELD_WRAPPER_CLASSES} ${showRecurrenceModifier(recurrence.frequency) ? "" : "hidden"}">
                    <label class="block text-base text-gray-500 sm:text-sm" id="task-recurrence-modifier-label">${getRecurrenceModifierLabel(recurrence.frequency)}${REQUIRED_MARK_HTML}</label>
                    <div data-task-recurrence-control="modifier"></div>
                </div>
                <div id="task-recurrence-anchor-wrap" class="${FIELD_WRAPPER_CLASSES} ${showRecurrenceAnchor(recurrence.frequency, recurrence.modifier) ? "" : "hidden"}">
                    ${getFieldLabelHtml("Calculate next from", true)}
                    <div data-task-recurrence-control="anchor"></div>
                </div>
            </div>
         </div>

         ${fieldHtml("Title", inputHtml({ name: "title", value: val("title"), required: true }), { required: true, className: "md:col-span-2" })}

         ${fieldHtml("Task Type(s)", '<div id="form-task-types-choices"></div>')}

         <div class="${FIELD_WRAPPER_CLASSES}">
             ${getFieldLabelHtml("Assign Users")}
             <div>
                 <div id="form-assigned_users-tags"></div>
             </div>
         </div>

         <div id="dynamic-fields-anchor" class="contents"></div>

         ${fieldHtml("Task Owner", '<div data-task-people-control="owner"></div>')}
         <div class="${FIELD_WRAPPER_CLASSES}"><label id="task-due-date-label" class="block text-base text-gray-500 sm:text-sm">Due Date${recurrence.isActive ? REQUIRED_MARK_HTML : ""}</label><div data-parent-scalar-control="due_date"></div></div>

         ${fieldHtml("Urgency", '<div data-parent-scalar-control="priority"></div>')}
         ${fieldHtml("Status", '<div data-parent-scalar-control="status"></div>')}

         ${fieldHtml("Details", textareaHtml({ name: "description", value: val("description") }), { className: "md:col-span-2" })}
      </div>
	 `;
}

function getTaskRecurrenceFormState(itemData = {}) {
  const recurrence = itemData?.recurrence || {};
  const frequency = recurrence.frequency || "";
  const isActive = !!frequency;

  return {
    isActive,
    frequency,
    modifier: recurrence.modifier || "",
    anchor: recurrence.anchor || "",
  };
}

function getWeekdayValueFromDate(dateString) {
  if (!dateString) {return "monday";}
  const parsedDate = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {return "monday";}

  const weekdayIndex = parsedDate.getDay();
  const weekdayMap = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  return weekdayMap[weekdayIndex] || "monday";
}

function showRecurrenceModifier(frequency) {
  return frequency === "weekly" || frequency === "monthly";
}

function showRecurrenceAnchor(frequency, modifier) {
  if (frequency === "daily" || frequency === "yearly") {return true;}
  if (frequency === "weekly") {return modifier === "same_day";}
  if (frequency === "monthly") {return modifier === "same_date";}
  return false;
}

function getRecurrenceModifierLabel(frequency) {
  if (frequency === "weekly") {return "Repeat on";}
  if (frequency === "monthly") {return "Repeat on";}
  return "Rule";
}

function getTaskRecurrenceModifierOptions(frequency, dueDate) {
  if (frequency === "weekly") {
    const weekday = getWeekdayValueFromDate(dueDate);
    return WEEKDAY_OPTIONS.map((option) => ({
      ...option,
      isDefault: option.value === weekday,
    }));
  }

  if (frequency === "monthly") {
    return MONTHLY_RECURRENCE_OPTIONS.map((option) => ({
      ...option,
      isDefault: option.value === "same_date",
    }));
  }

  return [];
}

/**
 * Wires task recurrence controls inside the active modal form.
 * @param {HTMLElement} formFields - Form fields container.
 * @param {object} itemData - Current modal form data.
 * @param {HTMLElement} formModalElement - Form modal root.
 * @param {object} [scalarControls] Registered scalar controls for the modal.
 */
export function setupTaskRecurrenceControls(
  formFields,
  itemData,
  formModalElement,
  scalarControls = {},
) {
  const toggleBtn = formModalElement?.querySelector("#task-recurrence-toggle");
  const recurrenceFields = formFields.querySelector("#task-recurrence-fields");
  const recurrenceEnabledInput = formFields.querySelector(
    'input[name="recurrence_enabled"]',
  );
  const frequencyRoot = formFields.querySelector(
    '[data-task-recurrence-control="frequency"]',
  );
  const modifierRoot = formFields.querySelector(
    '[data-task-recurrence-control="modifier"]',
  );
  const modifierWrap = formFields.querySelector(
    "#task-recurrence-modifier-wrap",
  );
  const modifierLabel = formFields.querySelector(
    "#task-recurrence-modifier-label",
  );
  const anchorWrap = formFields.querySelector("#task-recurrence-anchor-wrap");
  const anchorRoot = formFields.querySelector(
    '[data-task-recurrence-control="anchor"]',
  );
  const dueDateRoot = formFields.querySelector(
    '[data-parent-scalar-control="due_date"]',
  );
  const dueDateLabel = formFields.querySelector("#task-due-date-label");

  if (!toggleBtn || !recurrenceFields || !recurrenceEnabledInput ||
    !frequencyRoot || !modifierRoot || !modifierWrap ||
    !modifierLabel || !anchorWrap || !anchorRoot ||
    !dueDateLabel) {
    return;
  }

  const initialState = getTaskRecurrenceFormState(itemData);
  const dueDateControl = scalarControls.due_date || null;
  const isRecurrenceActive = () => recurrenceEnabledInput.value === "true";

  const frequencyControl = createSelectControl({
    root: frequencyRoot,
    name: "recurrence_frequency",
    value: initialState.frequency,
    options: RECURRENCE_FREQUENCY_OPTIONS,
    placeholder: "Select",
    clearable: false,
    disabled: !initialState.isActive,
    required: initialState.isActive,
    onChange: () => syncModifierOptions(),
  });
  const modifierControl = createSelectControl({
    root: modifierRoot,
    name: "recurrence_modifier",
    value: initialState.modifier,
    options: getTaskRecurrenceModifierOptions(
      initialState.frequency,
      dueDateControl?.value || itemData?.due_date || "",
    ),
    placeholder: "Select",
    clearable: false,
    disabled: !initialState.isActive ||
      !showRecurrenceModifier(initialState.frequency),
    required: initialState.isActive && showRecurrenceModifier(initialState.frequency),
    onChange: () => syncAnchorVisibility(),
  });
  const anchorControl = createSelectControl({
    root: anchorRoot,
    name: "recurrence_anchor",
    value: initialState.anchor,
    options: RECURRENCE_ANCHOR_OPTIONS,
    placeholder: "Select",
    clearable: false,
    disabled: !initialState.isActive ||
      !showRecurrenceAnchor(initialState.frequency, initialState.modifier),
    required: initialState.isActive &&
      showRecurrenceAnchor(initialState.frequency, initialState.modifier),
  });

  const setControlValue = (control, value) => {
    control.setValue(value, { silent: true });
  };

  const updateControl = (control, updates) => {
    control.update(updates);
  };

  const getMirrorValue = (name) => {
    return formModalElement?.querySelector(
      `input[name="${name}"][data-control-mirror]`,
    )?.value || "";
  };

  const getFrequencyValue = () => {
    return getMirrorValue("recurrence_frequency") || frequencyControl.value;
  };

  const getModifierValue = () => {
    return getMirrorValue("recurrence_modifier") || modifierControl.value;
  };

  const setToggleState = (isActive, options = {}) => {
    recurrenceEnabledInput.value = isActive ? "true" : "false";
    toggleBtn.setAttribute("aria-pressed", isActive ? "true" : "false");
    recurrenceFields.classList.toggle("hidden", !isActive);
    dueDateControl?.update({ required: isActive });
    updateControl(frequencyControl, {
      disabled: !isActive,
      required: isActive,
    });
    toggleBtn.style.color = isActive ? "#15803d" : "";
    dueDateLabel.innerHTML = `Due Date${isActive ? REQUIRED_MARK_HTML : ""}`;
    if (!isActive && options.clearValues) {
      setControlValue(frequencyControl, "");
      setControlValue(modifierControl, "");
      setControlValue(anchorControl, "");
    }
  };

  const getDueDateValue = () => dueDateControl?.value || itemData?.due_date || "";

  const hasOptionValue = (options, value) => {
    return options.some((option) => String(option.value) === String(value));
  };

  const syncAnchorVisibility = () => {
    const frequency = getFrequencyValue();
    const modifier = getModifierValue();
    const shouldShowAnchor = isRecurrenceActive() &&
      showRecurrenceAnchor(frequency, modifier);
    anchorWrap.classList.toggle("hidden", !shouldShowAnchor);
    updateControl(anchorControl, {
      disabled: !shouldShowAnchor,
      required: shouldShowAnchor,
    });
    if (!shouldShowAnchor && anchorControl.value) {
      setControlValue(anchorControl, "");
    }
  };

  const syncModifierOptions = () => {
    const frequency = getFrequencyValue();
    const shouldShowModifier = isRecurrenceActive() &&
      showRecurrenceModifier(frequency);
    const modifierOptions = getTaskRecurrenceModifierOptions(
      frequency,
      getDueDateValue(),
    );
    modifierWrap.classList.toggle("hidden", !shouldShowModifier);
    modifierLabel.innerHTML = `${getRecurrenceModifierLabel(frequency)}${REQUIRED_MARK_HTML}`;
    updateControl(modifierControl, {
      disabled: !shouldShowModifier,
      options: modifierOptions,
      required: shouldShowModifier,
    });

    if (!shouldShowModifier) {
      if (getModifierValue()) {setControlValue(modifierControl, "");}
      syncAnchorVisibility();
      return;
    }

    if (!hasOptionValue(modifierOptions, getModifierValue())) {
      setControlValue(modifierControl, "");
    }
    syncAnchorVisibility();
  };

  const frequencyMirrorInput = formModalElement?.querySelector(
    'input[name="recurrence_frequency"][data-control-mirror]',
  );
  const modifierMirrorInput = formModalElement?.querySelector(
    'input[name="recurrence_modifier"][data-control-mirror]',
  );

  frequencyMirrorInput?.addEventListener("change", syncModifierOptions);
  modifierMirrorInput?.addEventListener("change", syncAnchorVisibility);

  toggleBtn.addEventListener("click", () => {
    const isActivating = recurrenceEnabledInput.value !== "true";
    setToggleState(isActivating, { clearValues: !isActivating });
    syncModifierOptions();
  });

  dueDateRoot?.addEventListener("control:valuechange", () => {
    if (getFrequencyValue() === "weekly" ||
      getFrequencyValue() === "monthly") {
      syncModifierOptions();
    }
  });

  setToggleState(initialState.isActive);
  syncModifierOptions();
}
